import { IconButton, Popover, Text } from '@oneaudi/unified-web-components';
import { useCallback, useState } from 'react';
import { IncentivesSectionStyle } from '~/app/compositions/content/components/incentives-section-style';
import { useAppServices } from '~/app/contexts/app-context';
import { useI18n } from '~/app/i18n/i18n';
import { useMarketConfig } from '~/app/market-config';
import { appTrackingService } from '~/app/tracking/app-tracking-service';
import { mapTrackingIncentives } from '~/app/tracking/map-tracking-incentives';
import { Incentive } from '~/components/incentive/incentive';
import { Incentive as IncentiveType } from '~/graphql/__generated__/graphql';
import { useConfigurator } from '~/graphql/hooks/use-configurator';
import { CatalogItemType, FeatureCategory } from '~/graphql/types';
import { getSubtitle } from '~/utils/incentives';

export const IncentivesSection = ({ incentives }: { incentives: IncentiveType[] }) => {
	const { i18n } = useI18n();
	const { isItemLoading, changeConfiguration } = useConfigurator();
	const [isOpen, setIsOpen] = useState(false);
	const { formatter, market, language } = useMarketConfig();
	const {
		featureServices: { oneGraphClient },
	} = useAppServices();

	const handleSelect = useCallback(
		async (incentive: CatalogItemType['incentive'], componentName = 'configuration overview') => {
			// Tracking of the incentive select/deselect
			appTrackingService.incentivesClickEvent({
				eventName: `tacticals - click to ${incentive.status.selected ? 'deselect' : 'select'} discount`,
				componentName,
				label: incentive.action.title || '',
				elementName: componentName === 'configuration overview' ? 'check box' : 'button',
				relatedIncentive: mapTrackingIncentives([incentive]),
			});

			const refetchCarline = async () =>
				oneGraphClient.refetchQueries({
					include: ['Carline'],
				});

			const newIncentive = {
				...structuredClone(incentive),
				featureId: incentive.id,
				type: 'incentive' as FeatureCategory,
				name: 'incentive',
			};

			await changeConfiguration(newIncentive);
			await refetchCarline();
		},
		[changeConfiguration, incentives],
	);

	const handlePopoverClick = useCallback(() => {
		setIsOpen(!isOpen);

		appTrackingService.incentivesClickEvent({
			eventName: `tacticals - click to ${isOpen ? 'close' : 'open'} info popover`,
			componentName: 'configurator-configuration-overview',
			clickID: 'available discounts',
			elementName: 'icon',
		});
	}, [isOpen]);

	return incentives && incentives.length > 0 ? (
		<IncentivesSectionStyle>
			<div className="section-title">
				<Text tag="p" variant={{ type: 'copy', order: '1', style: 'normal' }}>
					{i18n('ngc.incentives.sectionTitle')}
				</Text>
				<Popover
					withTip={false}
					open={isOpen}
					onClose={() => setIsOpen(false)}
					id="incentive-popover"
					content={
						<Text tag="p" variant={{ type: 'copy', order: '2', style: 'normal' }}>
							{i18n('ngc.incentives.sectionTitle.tooltip')}
						</Text>
					}
					triggerElement={
						<IconButton
							variant="tertiary"
							size="sm"
							onClick={handlePopoverClick}
							icon="info-circle"
							aria-label={i18n('ngc.incentives.sectionTitle.tooltip.description')}
						/>
					}
				/>
			</div>
			<div className={'incentives-wrapper'}>
				{incentives &&
					incentives.map((incentive) => {
						return (
							<Incentive
								key={incentive.id}
								title={incentive.action.title}
								selected={incentive.status.selected}
								select={(componentName) => handleSelect(incentive, componentName)}
								price={incentive.price.value ? formatter.formatPrice(incentive.price.value) : undefined}
								subtitle={getSubtitle(
									i18n,
									incentive.action.description || '',
									incentive.validityPeriod.to || '',
									market,
									language,
								)}
								isLoading={isItemLoading({
									type: 'incentive' as FeatureCategory,
									featureId: incentive.id,
								})}
								incentive={incentive}
							/>
						);
					})}
			</div>
		</IncentivesSectionStyle>
	) : null;
};
